/**
 * Datadog RUM (Real User Monitoring) Client-Side Initialization
 *
 * Provides automatic page view tracking for SPA navigation via History API,
 * and distributed tracing correlation with APM backend traces.
 *
 * Features:
 * - Automatic view tracking on route changes (History API)
 * - RUM-APM correlation via allowedTracingUrls
 * - Privacy-first configuration (mask-user-input by default)
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/
 *
 * @example
 * ```typescript
 * import { initializeDatadogRum } from './lib/rum/datadogRum';
 *
 * // Initialize RUM (client-side only)
 * initializeDatadogRum(config);
 * ```
 */

import { datadogRum, RumErrorEvent } from '@datadog/browser-rum';

// Matches ONLY the browser's opaque cross-origin error placeholders — `Script error.`,
// `Script error`, or the wrapped `Uncaught "Script error."` (optional trailing period /
// surrounding quotes, case-insensitive). Anchored with ^…$ so a real first-party error
// whose message merely *contains* "script error" (e.g. `TypeError: script error handler
// is not a function`) is NOT matched.
const OPAQUE_CROSS_ORIGIN_SCRIPT_ERROR = /^(?:uncaught\s+["']?)?script error\.?["']?$/i;

const isCrossOriginScriptError = (event: RumErrorEvent): boolean => {
    // A cross-origin script that throws without CORS surfaces as an opaque, stackless
    // "Script error." (the browser hides the detail).
    //
    // IAFE-2627: after this change, our OWN scripts are loaded with crossorigin
    // (webpack `output.crossOriginLoading: 'anonymous'` + crossorigin on the SSR bootstrap
    // tags) and our CDN returns `Access-Control-Allow-Origin: *`, so their errors now
    // de-opaque into real messages/stacks and do NOT match here — they flow into RUM so we
    // can see and fix them. What remains opaque is only what we can't control CORS on:
    // browser extensions and third-party tags. Dropping that residue keeps unactionable,
    // un-attributable, non-blocking noise out of the RUM-based Frontend Availability SLO.
    //
    // NOTE (interim): this is a message denylist. Prefer shrinking the residue at the
    // source (get GlobalNav / 3P scripts to send CORS) and/or fixing the SLI definition so
    // extension noise is excluded structurally, rather than relying on this match forever.
    // (Original bug: the previous exact-equality check `=== 'script error.'` missed the
    // `Uncaught "Script error."` form — ~97% of RUM errors — so nothing was ever filtered.)
    const message = event.error?.message?.trim() ?? '';
    return OPAQUE_CROSS_ORIGIN_SCRIPT_ERROR.test(message);
};

/**
 * Configuration for Datadog RUM initialization
 */
export interface DatadogRumConfig {
    /** Datadog RUM Application ID */
    applicationId: string;
    /** Datadog client token (public, safe for browser) */
    clientToken: string;
    /** Datadog site (e.g., 'datadoghq.com', 'datadoghq.eu') */
    site: string;
    /** Service name (must match APM service name for correlation) */
    service: string;
    /** Environment name (must match APM env for correlation) */
    env: string;
    /** Application version */
    version: string;
    /** GraphQL endpoint URL for APM trace correlation */
    graphqlEndpoint?: string;
}

/**
 * Initializes Datadog RUM for client-side monitoring
 *
 * This function should only be called from client-side code (e.g., App.client.tsx).
 * It automatically tracks:
 * - Page views via History API (pushState, replaceState, popstate)
 * - User interactions (clicks, etc.)
 * - Resources and long tasks
 * - Errors
 *
 * For RUM-APM correlation, it injects Datadog trace headers into
 * requests matching allowedTracingUrls.
 */
export const initializeDatadogRum = (config: DatadogRumConfig): void => {
    // Validate required configuration
    if (!config.applicationId || !config.clientToken) {
        console.warn('[DatadogRUM] Skipping initialization: missing applicationId or clientToken');
        return;
    }

    try {
        datadogRum.init({
            // Core identification
            applicationId: config.applicationId,
            clientToken: config.clientToken,
            site: config.site || 'datadoghq.com',

            // Service identification (MUST match APM service name for correlation)
            service: config.service,
            env: config.env,
            version: config.version,

            // Sampling Configuration
            sessionSampleRate: 100,
            sessionReplaySampleRate: 0,

            // Feature Tracking
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,

            // Privacy Configuration
            defaultPrivacyLevel: 'mask-user-input',

            // RUM-APM Correlation (Distributed Tracing)
            allowedTracingUrls: config.graphqlEndpoint
                ? [
                      {
                          match: config.graphqlEndpoint,
                          propagatorTypes: ['tracecontext', 'datadog'] as const,
                      },
                  ]
                : [],
            traceSampleRate: 100,

            // Filter out cross-origin script errors from GlobalNav and other third-party scripts
            beforeSend: (event) => !(event.type === 'error' && isCrossOriginScriptError(event)),
        });

        console.log('[DatadogRUM] Initialized successfully', {
            applicationId: config.applicationId.substring(0, 8) + '...',
            env: config.env,
            service: config.service,
        });
    } catch (error) {
        console.error('[DatadogRUM] Failed to initialize:', error);
    }
};
